import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Divider, Grid, Group, LoadingOverlay, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { useAppDispatch, useAppSelector } from '../../../store'
import { VehicleType } from '../../../store/types'
import { BackListButton, ContentBox, ItemActionsMenu } from '../../partials'
import { fetchShowVehicle, resetShowVehicle } from '../../../store/features/vehicles/showVehicleSlice'
import { fetchDeleteVehicle, resetDeleteVehicle } from '../../../store/features/vehicles/deleteVehicleSlice'

const Show = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [data, setData] = useState<VehicleType | null>(null)
  const { isLoading, response, error } = useAppSelector(state => state.showVehicle)
  const {
    isLoading: deleteIsLoading,
    response: deleteResponse,
    error: deleteError
  } = useAppSelector(state => state.deleteVehicle)

  useEffect(() => {
    if (response === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowVehicle(id as string))
      })()
    }
  }, [response, data, id, dispatch])

  useEffect(() => {
    if (response !== null) {
      setData(response.data)

      if (data !== null) {
        dispatch(resetShowVehicle())
      }
    }
  }, [response, dispatch, data])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowVehicle())
          await dispatch(fetchShowVehicle(id))
        })()
      } else if (error.response.status === 404) {
        dispatch(resetShowVehicle())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(error.data.message)
        })

        navigate('/vehicles')
      } else {
        dispatch(resetShowVehicle())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/vehicles')
      }
    }
  }, [error, id, dispatch, t, navigate])

  useEffect(() => {
    if (deleteResponse !== null) {
      dispatch(resetShowVehicle())
      dispatch(resetDeleteVehicle())

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('deleted')
      })

      navigate('/vehicles')
    }
  }, [deleteResponse, t, dispatch, navigate])

  useEffect(() => {
    if (deleteError !== null) {
      dispatch(resetShowVehicle())

      showNotification({
        color: 'red',
        title: t('unsuccessful'),
        message: deleteError.response.data ? deleteError.response.data.message : t('errors.unknown')
      })
    }
  }, [deleteError, t, dispatch])

  const handleDelete = async (id: string) => {
    await dispatch(fetchDeleteVehicle(id))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={isLoading || deleteIsLoading} />
      {data && (
        <>
          <ContentBox title={t('content_actions.show')}>
            <h4>{t('id')}:</h4>
            <Text>{data.id}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>Model Name:</h4>
            <Text>{data.model.name}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>Model Detail Name:</h4>
            <Text>{data.model_detail.name}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>Model Year:</h4>
            <Text>{data.model_year}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />
            <h4>Brand Name:</h4>
            <Text>{data.brand.name}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />
            <h4>Group Name:</h4>
            <Text>{data.group_name ? data.group_name : '-'}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />
            <h4>Class Name:</h4>
            <Text>{data.class_name ? data.class_name : '-'}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />
            <h4>Condition:</h4>
            <Text>{t(`conditions.${data.condition}`)}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />
          </ContentBox>
          <ContentBox>
            <Grid>
              <Grid.Col span={6}>
                <BackListButton to="/vehicles" />
              </Grid.Col>
              <Grid.Col span={6}>
                <Group
                  position="right"
                  pr="md"
                >
                  <ItemActionsMenu
                    edit={`/vehicles/${data.id}/edit`}
                    onDelete={() => handleDelete(data.id)}
                  />
                </Group>
              </Grid.Col>
            </Grid>
          </ContentBox>
        </>
      )}
    </MainLayout>
  )
}

export default Show
